<template>
  <div data-testid="game-list">
    <!-- Header: title, sort dropdown, search -->
    <div
      class="flex flex-col gap-6 pb-8 lg:flex-row lg:items-center lg:justify-between"
    >
      <h1
        v-if="selectedSearchType.type !== SearchTypes.SEARCH_PRODUCT_GROUP"
        class="uppercase"
        data-testid="selected-sort-type"
      >
        {{ t(`landingpage/index/games/title/${selectedSearchType.key}`) }}
      </h1>
      <h1 v-else class="uppercase" data-testid="selected-sort-type">
        {{ selectedSearchType.label }}
      </h1>

      <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div class="w-full sm:w-48">
          <form-dropdown
            :label="t('landingpage/index/games/sort_by')"
            :items="productGroupsData"
            item-class="capitalize"
            :value="selectedSearchType"
            @change="selectSearchType"
          />
        </div>

        <!-- Platform filter toggles -->
        <div
          class="flex items-center gap-1.5"
          role="group"
          :aria-label="t('landingpage/index/games/platform/filter')"
        >
          <button
            v-for="pf in platformFilters"
            :key="pf.value"
            type="button"
            :aria-label="pf.label"
            :aria-pressed="activePlatforms.has(pf.value)"
            class="flex size-9 items-center justify-center rounded-md border transition-colors"
            :class="
              activePlatforms.has(pf.value)
                ? 'border-brand/40 bg-brand/10 text-brand'
                : 'border-rhino/20 bg-depth-1 text-rhino hover:text-mouse hover:border-rhino/40'
            "
            @click="togglePlatform(pf.value)"
          >
            <fa-icon :icon="pf.icon" class="size-4" />
          </button>
        </div>

        <div class="relative w-full sm:w-64">
          <div
            class="pointer-events-none absolute inset-y-0 inset-s-0 flex items-center ps-3"
            aria-hidden="true"
          >
            <fa-icon :icon="faMagnifyingGlass" class="size-4 text-rhino" />
          </div>
          <input
            v-model="searchString"
            data-testid="game-search-input"
            type="text"
            class="block w-full rounded-md border border-rhino/20 bg-depth-1 py-2 ps-10 pe-3 text-mouse outline-none placeholder:text-rhino transition-colors focus:border-brand"
            :placeholder="t('landingpage/index/search')"
            :aria-label="t('landingpage/index/search')"
          />
        </div>
      </div>
    </div>

    <!-- Game grid -->
    <div
      v-if="sortedGames.length > 0"
      class="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    >
      <gameserver-index-game-card
        v-for="game in sortedGames"
        :key="game.id"
        :game="game"
      />
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex flex-col items-center justify-center gap-4 py-16 text-center"
    >
      <fa-icon
        :icon="faGamepadModern"
        class="size-10 text-rhino"
        aria-hidden="true"
      />
      <p class="text-rhino">
        {{ t('landingpage/index/no_results') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/nuxt';
import { faMagnifyingGlass, faDesktop } from '@fortawesome/pro-solid-svg-icons';
import { faGamepadModern } from '@fortawesome/pro-light-svg-icons';
import { faXbox, faPlaystation } from '@fortawesome/free-brands-svg-icons';
import {
  GetGames,
  type GetGamesQuery,
  type GetGamesQueryVariables,
  GetProductGroups,
  type GetProductGroupsQuery,
  type GetProductGroupsQueryVariables,
  StrapiPlatform,
  type StrapiProduct,
  type StrapiProductGroup,
} from '~/apollo/types/types';

enum SearchTypes {
  SEARCH_A_TO_Z = 1,
  SEARCH_Z_TO_A,
  SEARCH_PRODUCT_GROUP,
}

interface SearchType {
  type: SearchTypes;
  key: string;
  label: string;
}

const { t, locale } = useI18n();
const currencyStore = useCurrencyStore();

const searchString = ref('');
const debouncedSearchString = ref('');

const platformFilters = [
  { value: StrapiPlatform.Pc, label: 'PC', icon: faDesktop },
  { value: StrapiPlatform.Ps4, label: 'PlayStation', icon: faPlaystation },
  { value: StrapiPlatform.XBox, label: 'Xbox', icon: faXbox },
];

const activePlatforms = ref(new Set<StrapiPlatform>());

function togglePlatform(platform: StrapiPlatform) {
  const next = new Set(activePlatforms.value);
  if (next.has(platform)) {
    next.delete(platform);
  } else {
    next.add(platform);
  }
  activePlatforms.value = next;
}

// Computed so the A→Z / Z→A labels rebuild when the locale changes; the
// locale-keyed product-groups fetch below re-reads defaultSearchTypes.value, so
// the dropdown options pick up the new-locale labels after a client-side switch.
const defaultSearchTypes = computed<SearchType[]>(() => [
  {
    type: SearchTypes.SEARCH_A_TO_Z,
    key: 'a_to_z',
    label: t('landingpage/index/games/sort/dropdown/a_to_z').toString(),
  },
  {
    type: SearchTypes.SEARCH_Z_TO_A,
    key: 'z_to_a',
    label: t('landingpage/index/games/sort/dropdown/z_to_a').toString(),
  },
]);

const selectedSearchType = ref<SearchType>(defaultSearchTypes.value[0]);
const searchTypeBlacklist = ['Footer', 'Advert'];

const currentCurrency = computed(() =>
  currencyStore.currency.length === 0 ? 'EUR' : currencyStore.currency,
);

// Sort options come from Strapi product groups and depend only on the locale,
// so they are fetched independently of the game search. This keeps the sort
// dropdown populated across searches and back-navigation.
const { data: productGroupsData } = await useAsyncData(
  () => `gp-gameserver-index-product-groups-${locale.value}`,
  async () => {
    try {
      const res = await useLegacyApollo<
        GetProductGroupsQuery,
        GetProductGroupsQueryVariables
      >(GetProductGroups, {
        locale: locale.value,
      });

      const productGroups = (res.data.productGroups ??
        []) as StrapiProductGroup[];
      const additionalSearchTypes = [...productGroups]
        .reverse()
        .filter((pg) => !searchTypeBlacklist.includes(pg.groupKey))
        .map<SearchType>((pg) => ({
          type: SearchTypes.SEARCH_PRODUCT_GROUP,
          key: pg.groupKey,
          label: pg.slug,
        }));

      return [...defaultSearchTypes.value, ...additionalSearchTypes];
    } catch (error: unknown) {
      captureException(error);

      return [...defaultSearchTypes.value];
    }
  },
  {
    default: () => [...defaultSearchTypes.value],
  },
);

// The cache key is a reactive getter so each distinct query (search term, sort
// type, currency, locale) maps to its own cache entry. Returning to the index
// with an empty search resolves to the "all games" entry instead of a stale
// filtered result.
const { refresh, data: gameListData } = await useAsyncData(
  () =>
    `gp-gameserver-index-games-${debouncedSearchString.value}-${locale.value}-${currentCurrency.value}-${selectedSearchType.value.type}-${selectedSearchType.value.key}`,
  async () => {
    const groups: string[] = [];
    if (selectedSearchType.value.type === SearchTypes.SEARCH_PRODUCT_GROUP) {
      groups.push(selectedSearchType.value.key);
    }

    try {
      const { data: response } = await useLegacyApollo<
        GetGamesQuery,
        GetGamesQueryVariables
      >(GetGames, {
        search:
          debouncedSearchString.value !== ''
            ? debouncedSearchString.value
            : null,
        locale: locale.value,
        groups,
        currency: currentCurrency.value,
      });

      return { games: (response.products ?? []) as StrapiProduct[] };
    } catch (error: unknown) {
      captureException(error);

      return { games: [] as StrapiProduct[] };
    }
  },
  {
    dedupe: 'defer',
    default: () => ({ games: [] as StrapiProduct[] }),
  },
);

const games = computed<StrapiProduct[]>(() => gameListData.value.games);

useUserCurrency(refresh, refresh);

const syncDebouncedSearch = useDebounceFn((value: string) => {
  debouncedSearchString.value = value;
}, 150);

watch(searchString, (value) => syncDebouncedSearch(value));

const selectSearchType = (st: SearchType) => {
  selectedSearchType.value = st;
};

const sortedGames = computed<StrapiProduct[]>(() => {
  if (games.value == null) {
    return [];
  }

  let filtered = games.value;
  if (activePlatforms.value.size > 0) {
    filtered = filtered.filter((g) => activePlatforms.value.has(g.platform));
  }

  switch (selectedSearchType.value.type) {
    case SearchTypes.SEARCH_Z_TO_A:
      return [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    default:
      return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }
});
</script>
